import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../use-api';
import { api, VideoDetail, Language, Quality } from '../api';
import { Layout, Loading, ErrorBox } from '../components/Layout';

const LANG_LABEL: Record<Language, string> = {
  PS: 'پښتو',
  DA: 'دری',
  EN: 'English',
};

export function Watch() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data, error, loading } = useApi<VideoDetail>(() => api.video(id!), [id]);

  const [language, setLanguage] = useState<Language | null>(null);
  const [quality, setQuality] = useState<Quality | null>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const retried = useRef(false);

  // Default to the first available variant, preferring English/HD if present.
  useEffect(() => {
    if (!data || language) return;
    const langs = [...new Set(data.variants.map((v) => v.language))];
    const quals = [...new Set(data.variants.map((v) => v.quality))];
    setLanguage(langs.includes('EN') ? 'EN' : langs[0] ?? null);
    setQuality(quals.includes('HD') ? 'HD' : quals[0] ?? null);
  }, [data, language]);

  const hasVariant =
    !!data && !!language && !!quality &&
    data.variants.some((v) => v.language === language && v.quality === quality);

  // Mint a fresh stream URL whenever the chosen variant changes. Lazily —
  // one link, for the lesson actually being watched (spec §7).
  useEffect(() => {
    if (!id || !language || !quality || !hasVariant) {
      setSrc(null);
      return;
    }
    let live = true;
    retried.current = false;
    setMintError(null);
    api
      .access(id, language, quality, 'stream')
      .then((r) => live && setSrc(r.url))
      .catch(() => live && setMintError(t('common.error')));
    return () => {
      live = false;
    };
  }, [id, language, quality, hasVariant, t]);

  /**
   * The signed URL outlives the player only for its 1h TTL. If a lesson is
   * paused past that and then seeked, the media route answers 403 and the
   * <video> element surfaces a generic decode error. Re-mint once and resume
   * from the same offset — the user should never see this happen.
   */
  async function onVideoError() {
    if (!id || !language || !quality || retried.current) return;
    retried.current = true;
    const at = videoRef.current?.currentTime ?? 0;
    try {
      const r = await api.access(id, language, quality, 'stream');
      setSrc(r.url);
      const el = videoRef.current;
      if (el) {
        const restore = () => {
          el.currentTime = at;
          el.removeEventListener('loadedmetadata', restore);
        };
        el.addEventListener('loadedmetadata', restore);
      }
    } catch {
      setMintError(t('common.error'));
    }
  }

  async function download() {
    if (!id || !language || !quality) return;
    setDownloading(true);
    try {
      // A separate mint with kind=download: it is logged as a download and
      // signed with dl=1, which is what makes the server send an attachment.
      const r = await api.access(id, language, quality, 'download');
      const a = document.createElement('a');
      a.href = r.url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      setMintError(t('common.error'));
    } finally {
      setDownloading(false);
    }
  }

  const languages = data ? [...new Set(data.variants.map((v) => v.language))] : [];
  const qualities = data ? [...new Set(data.variants.map((v) => v.quality))] : [];

  return (
    <Layout>
      {loading && <Loading />}
      {error && <ErrorBox message={error} />}
      {data && (
        <>
          <Link to={`/book/${data.book.id}`} className="text-sm text-sky-700 hover:underline">
            ← {data.book.title}
          </Link>
          <h1 className="text-2xl font-semibold text-slate-900 mt-2 mb-4">{data.title}</h1>

          <div className="rounded-xl overflow-hidden bg-black">
            {src ? (
              <video
                ref={videoRef}
                key={src}
                src={src}
                controls
                playsInline
                onError={() => void onVideoError()}
                className="w-full aspect-video"
              />
            ) : (
              <div className="w-full aspect-video grid place-items-center text-slate-400 text-sm">
                {hasVariant ? t('watch.preparing') : t('watch.noVariant')}
              </div>
            )}
          </div>

          {mintError && (
            <div className="mt-3">
              <ErrorBox message={mintError} />
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div>
              <label htmlFor="lang" className="block text-sm font-medium text-slate-700 mb-1">
                {t('watch.audio')}
              </label>
              <select
                id="lang"
                value={language ?? ''}
                onChange={(e) => setLanguage(e.target.value as Language)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                {languages.map((l) => (
                  <option key={l} value={l}>
                    {LANG_LABEL[l]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="quality" className="block text-sm font-medium text-slate-700 mb-1">
                {t('watch.quality')}
              </label>
              <select
                id="quality"
                value={quality ?? ''}
                onChange={(e) => setQuality(e.target.value as Quality)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                dir="ltr"
              >
                {qualities.map((q) => (
                  <option key={q} value={q}>
                    {q}
                  </option>
                ))}
              </select>
            </div>

            {/*
              Phase 4 §6: shown only when this viewer holds download rights for
              this lesson. Hidden rather than disabled — a greyed-out button
              reads as "temporarily unavailable" and invites support tickets,
              where absence reads as "not part of your access". The media
              routes refuse it either way, so this is presentation only.
            */}
            {data.canDownload && (
              <button
                onClick={() => void download()}
                disabled={!hasVariant || downloading}
                className="rounded-md bg-sky-600 px-4 py-2 text-white text-sm font-medium hover:bg-sky-700 disabled:opacity-50"
              >
                {downloading ? t('watch.preparing') : t('watch.download')}
              </button>
            )}
          </div>
        </>
      )}
    </Layout>
  );
}
